import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { searchProducts } from '../../utils/algolia';

const initialState = {
  searchTerm: '',
  sortBy: 'products',
  filters: {
    productType: '',
    forRent: false,
    forSell: false,
    openToSwap: false,
    openToShipping: undefined,
    localPickup: {
      enabled: false
    },
    location: null,
    categories: '',
    sizes: '',
    colors: '',
    occasions: '',
    styles: '',
    genders: ''
  },
  loading: false,
  dataLoading: false,
  error: null,
  products: [],
  totalResults: 0,
  offset: 0,
  hasMore: false,
  isInitialized: false,
  lastSearch: null,
  pageType: null, // 'browse' | 'listings' | null
  layoutChanging: false // Flag to prevent loading during layout changes
};

// Parse URL parameters
const parseUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  const searchTerm = params.get('q') || '';
  const sortBy = params.get('sort') || 'products';
  const filters = {};

  params.forEach((value, key) => {
    if (key !== 'q' && key !== 'sort') {
      try {
        filters[key] = JSON.parse(value);
      } catch {
        filters[key] = value;
      }
    }
  });

  return { searchTerm, sortBy, filters };
};

// Search thunk
export const searchWithAlgolia = createAsyncThunk(
  'filter/searchWithAlgolia',
  async ({ searchTerm, filters, sortBy, offset = 0 }, { rejectWithValue, getState }) => {
    try {
      const state = getState().filter;
      const location = state.filters.location && state.filters.localPickup?.enabled
        ? state.filters.location
        : null;
      
      const result = await searchProducts({ 
        searchTerm, 
        filters, 
        sortBy, 
        offset,
        location
      }, getState);

      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Search with term thunk - preserves filters
export const searchWithTerm = createAsyncThunk(
  'filter/searchWithTerm',
  async (searchTerm, { dispatch, getState }) => {
    const state = getState().filter;
    
    // Update the search term without resetting other filters
    dispatch(setSearchTerm(searchTerm));
    
    // Perform search with updated term and current filters
    return dispatch(searchWithAlgolia({ 
      searchTerm, 
      filters: state.filters, 
      sortBy: state.sortBy, 
      offset: 0 
    }));
  }
);

// URL change listener thunk
export const handleUrlChange = createAsyncThunk(
  'filter/handleUrlChange',
  async (_, { dispatch, getState }) => {
    const { searchTerm, sortBy, filters } = parseUrlParams();
    const state = getState().filter;

    // Only proceed if we're on a valid page type
    if (!state.pageType) {
      return null;
    }

    // Check if any values have changed from the last successful search
    const hasChanged = !state.lastSearch || 
      searchTerm !== state.lastSearch.searchTerm ||
      sortBy !== state.lastSearch.sortBy ||
      JSON.stringify(filters) !== JSON.stringify(state.lastSearch.filters);

    // Skip if we're already loading data and nothing has changed
    if (state.dataLoading && !hasChanged) {
      return null;
    }

    // Update parameters without triggering loading state
    dispatch(updateAllParams({ 
      searchTerm, 
      sortBy, 
      filters,
      setLoading: false // Don't set loading here, let searchWithAlgolia handle it
    }));

    // Only trigger search if parameters have changed or not initialized
    if (hasChanged || !state.isInitialized) {
      return dispatch(searchWithAlgolia({ searchTerm, filters, sortBy, offset: 0 }));
    }
    
    return null;
  }
);

// Clear filters and trigger search thunk
export const clearAllAndSearch = createAsyncThunk(
  'filter/clearAllAndSearch',
  async (_, { dispatch, getState }) => {
    // First clear the filters
    dispatch(clearAll());
    
    try {
      // Then trigger a search with empty parameters
      const result = await dispatch(searchWithAlgolia({ 
        searchTerm: '', 
        filters: {}, 
        sortBy: 'products', 
        offset: 0 
      })).unwrap();
      
      // Make sure loading states are reset
      dispatch(setDataLoading(false));
      
      return result;
    } catch (error) {
      // Ensure loading states are reset even on error
      dispatch(setDataLoading(false));
      return Promise.reject(error);
    }
  }
);

const filterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    updateAllParams: (state, action) => {
      const { searchTerm, sortBy, filters, setLoading = true } = action.payload;
      state.searchTerm = searchTerm;
      state.sortBy = sortBy;
      state.filters = {
        ...state.filters,
        ...filters
      };
      state.offset = 0;
      state.isInitialized = true;
      if (setLoading) {
        state.dataLoading = true;
      }
    },
    setDataLoading: (state, action) => {
      state.dataLoading = action.payload;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
      state.offset = 0;
      state.dataLoading = true;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
      state.offset = 0;
      state.dataLoading = true;
    },
    setFilter: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      };
      state.offset = 0;
      state.dataLoading = true;
    },
    setPageType: (state, action) => {
      const newPageType = action.payload;
      const oldPageType = state.pageType;
      
      // Only proceed if the page type is actually changing
      if (newPageType !== oldPageType) {
        if (newPageType === null) {
          // When unmounting, just clear the page type
          state.pageType = null;
        } else if (newPageType === 'browse' || newPageType === 'listings' || newPageType === 'favorites' || newPageType === 'merchant') {
          // Reset state for new page type
          Object.assign(state, {
            ...initialState,
            pageType: newPageType,
            isInitialized: false // Let handleUrlChange handle the initialization
          });
        }
      }
    },
    clearAll: (state) => {
      const { pageType } = state;
      Object.assign(state, {
        ...initialState,
        pageType,
        loading: false,
        dataLoading: false,
        isInitialized: true,
        hasMore: false,
        error: null
      });
    },
    setLayoutChanging: (state, action) => {
      state.layoutChanging = action.payload;
    },
    loadMore: (state) => {
      if (state.hasMore && !state.loading && !state.layoutChanging) {
        state.offset += 20;
        state.dataLoading = true;
      }
    },
    optimisticUpdate: (state, action) => {
      state.products = action.payload;
      state.totalResults = action.payload.length;
      state.offset = 0;
      state.hasMore = false;
      state.dataLoading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchWithAlgolia.pending, (state) => {
        state.loading = true;
        state.dataLoading = true;
        state.error = null;
      })
      .addCase(searchWithAlgolia.fulfilled, (state, action) => {
        const offset = action.meta.arg.offset || 0;
        const { searchTerm, filters, sortBy } = action.meta.arg;
        
        if (offset === 0) {
          state.products = action.payload.products;
          state.offset = 0;
        } else {
          state.products = [...state.products, ...action.payload.products];
          state.offset = offset;
        }
        
        state.totalResults = action.payload.totalResults;
        state.hasMore = state.products.length < action.payload.totalResults;
        state.loading = false;
        state.dataLoading = false;
        state.isInitialized = true;
        state.lastSearch = { searchTerm, filters, sortBy };
      })
      .addCase(searchWithAlgolia.rejected, (state, action) => {
        state.loading = false;
        state.dataLoading = false;
        state.error = action.payload;
      });
  }
});

export const { 
  updateAllParams,
  setDataLoading,
  setSearchTerm, 
  setSortBy, 
  setFilter,
  setPageType,
  clearAll, 
  loadMore,
  optimisticUpdate,
  setLayoutChanging
} = filterSlice.actions;

export default filterSlice.reducer; 